// src/pages/Tasks.jsx
// getTsk   → list    { data: [{id,title,status,fromusr,tousr,deadline,detail}] }
// getTskDtl→ detail  { data: {id,..., indetail:[{detail,date,user_id}]} }
// tasManage→ create (no id) or update status + reply (has id)
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Tasks as API, TeamMembers } from '../services/api';
import { useAuth } from '../context/AuthContext';

const STAT_COLOR = { assigned:'var(--accent)', process:'var(--yellow)',
  delayed:'var(--red)', cancelled:'var(--faint)', deferred:'var(--purple)', done:'var(--green)' };
const STAT_OPTS  = ['assigned','process','delayed','cancelled','deferred','done'];

export default function Tasks() {
  const { user }  = useAuth();
  const bottomRef = useRef(null);
  const [tasks, setTasks]       = useState([]);
  const [detail, setDetail]     = useState(null);
  const [members, setMembers]   = useState([]);
  const [filter, setFilter]     = useState('open');
  const [showNew, setShowNew]   = useState(false);
  const [replyText, setReply]   = useState('');
  const [busy, setBusy]         = useState(true);
  const [detBusy, setDetBusy]   = useState(false);
  const [newTask, setNewTask]   = useState({ title:'', tousr:'', deadline:'', detail:'' });
  const [loadErr, setLoadErr]   = useState('');

  const loadList = useCallback(() => {
    setBusy(true);
    setLoadErr('');
    Promise.all([API.list(), TeamMembers.list('y')]).then(([t, u]) => {
      setTasks(t);
      setMembers(u);
    }).catch(ex => setLoadErr(ex.message || 'Failed to load tasks'))
      .finally(() => setBusy(false));
  }, []);
  useEffect(loadList, [loadList]);

  const openDetail = (t) => {
    setDetBusy(true);
    API.detail(t.id).then(d => {
      setDetail(d);
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    }).finally(() => setDetBusy(false));
  };

  // Mobile: going back means clearing the open task, which is exactly
  // what switches the view back to the list via the CSS below.
  const closeDetail = () => setDetail(null);

  const sendReply = async () => {
    if (!replyText.trim() || !detail) return;
    await API.update({ id: detail.id, user_id: user.id, status: detail.status, newDtl: replyText });
    setReply('');
    openDetail(detail);
  };

  const changeStatus = async (status) => {
    await API.update({ id: detail.id, user_id: user.id, status, newDtl: '' });
    openDetail(detail);
    loadList();
  };

  const createTask = async () => {
    if (!newTask.title || !newTask.tousr || !newTask.deadline) {
      alert('Title, assignee and deadline required'); return;
    }
    await API.create({ ...newTask, corp_id: user.corp_id, fromusr: user.id, tousr: +newTask.tousr });
    setShowNew(false);
    setNewTask({ title:'', tousr:'', deadline:'', detail:'' });
    loadList();
  };

  const userNameById = (id) => members.find(m => m.id == id)?.name || `User ${id}`;

  const filtered = tasks.filter(t =>
    filter === 'open'
      ? !['done','cancelled'].includes(t.status)
      : ['done','cancelled'].includes(t.status)
  );

  const setN = k => e => setNewTask(p => ({ ...p, [k]: e.target.value }));

  return (
    <div className={`task-shell${detail ? ' detail-open' : ''}`}>
      {/* Responsive rules: on screens under 860px, show list OR detail,
          never both squeezed side-by-side. Above that width, both panels
          sit side-by-side as before. */}
      <style>{`
        .task-shell {
          display: flex;
          height: calc(100vh - 52px);
          overflow: hidden;
        }
        .task-list-panel {
          width: 280px;
          border-right: 1px solid var(--border);
          display: flex;
          flex-direction: column;
          flex-shrink: 0;
          background: var(--bg);
        }
        .task-detail-panel {
          flex: 1;
          display: flex;
          flex-direction: column;
          overflow: hidden;
        }
        @media (max-width: 860px) {
          .task-list-panel { width: 100%; }
          .task-detail-panel { position: absolute; inset: 0; background: var(--bg); }
          .task-shell:not(.detail-open) .task-detail-panel { display: none; }
          .task-shell.detail-open .task-list-panel { display: none; }
          .task-shell { position: relative; }
        }
      `}</style>

      {/* ── List panel ── */}
      <div className="task-list-panel">
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'.9rem 1rem', borderBottom:'1px solid var(--border)' }}>
          <span style={{ fontWeight:700, color:'var(--text)' }}>✅ Tasks</span>
          <button className="btn btn-primary btn-sm" onClick={() => setShowNew(true)}>+ New</button>
        </div>
        <div style={{ display:'flex', borderBottom:'1px solid var(--border)' }}>
          {['open','done'].map(f => (
            <button key={f} onClick={() => setFilter(f)}
              style={{ flex:1, padding:'.5rem', border:'none', background:'transparent', cursor:'pointer',
                color: filter===f ? 'var(--accent)' : 'var(--muted)',
                borderBottom: filter===f ? '2px solid var(--accent)' : '2px solid transparent', fontSize:'.82rem' }}>
              {f === 'open' ? 'Open' : 'Closed'}
            </button>
          ))}
        </div>
        {loadErr && <div className="alert alert-error" style={{ margin: '.6rem' }}>{loadErr}</div>}
        <div style={{ flex:1, overflowY:'auto' }}>
          {busy ? <div className="spinner-wrap"><div className="spinner" /></div> :
            filtered.length === 0 ? <div className="empty">No {filter} tasks</div> :
            filtered.map(t => (
              <div key={t.id} onClick={() => openDetail(t)}
                style={{ padding:'.8rem 1rem', borderBottom:'1px solid var(--border)', cursor:'pointer',
                  background: detail?.id == t.id ? 'var(--surface)' : 'transparent',
                  display:'flex', gap:'.5rem', alignItems:'flex-start' }}>
                <div style={{ width:8, height:8, borderRadius:'50%', background:STAT_COLOR[t.status]||'var(--faint)', marginTop:6, flexShrink:0 }} />
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontWeight:600, color:'var(--text)', fontSize:'.88rem', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{t.title}</div>
                  <div style={{ color:'var(--faint)', fontSize:'.73rem', marginTop:2 }}>→ {userNameById(t.tousr)} · {t.deadline}</div>
                </div>
              </div>
            ))
          }
        </div>
      </div>

      {/* ── Detail panel ── */}
      <div className="task-detail-panel">
        {!detail ? (
          <div className="flex-center" style={{ flex:1, flexDirection:'column', color:'var(--faint)', gap:'.5rem' }}>
            <span style={{ fontSize:'2.5rem' }}>✅</span>
            <span>Select a task to view details</span>
          </div>
        ) : detBusy ? <div className="spinner-wrap"><div className="spinner" /></div> : (
          <>
            {/* Header — back button only shows/matters on mobile via CSS above */}
            <div style={{ padding:'1rem 1.2rem', borderBottom:'1px solid var(--border)', display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:'1rem', flexShrink:0 }}>
              <div style={{ display: 'flex', gap: '.7rem', alignItems: 'flex-start', minWidth: 0 }}>
                <button
                  onClick={closeDetail}
                  className="task-back-btn"
                  style={{ display: 'none', background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.2rem', color: 'var(--muted)', flexShrink: 0, padding: '2px 4px 0 0' }}
                  title="Back to list"
                >
                  ←
                </button>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight:700, color:'var(--text)', fontSize:'1.05rem' }}>{detail.title}</div>
                  <div style={{ color:'var(--muted)', fontSize:'.78rem', marginTop:2 }}>
                    {userNameById(detail.fromusr)} → {userNameById(detail.tousr)} · Due: {detail.deadline}
                  </div>
                </div>
              </div>
              <select style={{ width:'auto', flexShrink: 0 }} value={detail.status} onChange={e => changeStatus(e.target.value)}>
                {STAT_OPTS.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>

            {/* Task detail text */}
            {detail.detail && (
              <div style={{ padding:'.8rem 1.2rem', background:'var(--bg)', borderBottom:'1px solid var(--border)', color:'var(--muted)', fontSize:'.88rem', flexShrink:0 }}>
                {detail.detail}
              </div>
            )}

            {/* Chat thread */}
            <div style={{ flex:1, overflowY:'auto', padding:'1rem 1.2rem', display:'flex', flexDirection:'column', gap:'.6rem' }}>
              {(detail.indetail || []).map((r, i) => {
                const isMe = String(r.user_id) === String(user.id);
                return (
                  <div key={i} style={{ display:'flex', flexDirection:'column', alignSelf: isMe ? 'flex-end' : 'flex-start', maxWidth:'70%' }}>
                    {!isMe && <div style={{ color:'var(--faint)', fontSize:'.72rem', marginBottom:2 }}>{userNameById(r.user_id)}</div>}
                    <div style={{ padding:'.55rem .9rem', borderRadius:10, fontSize:'.88rem',
                      background: isMe ? '#1d4ed8' : 'var(--raised)', color:'var(--text)' }}>
                      {r.detail}
                    </div>
                    <div style={{ color:'var(--faint)', fontSize:'.7rem', marginTop:2 }}>{r.date}</div>
                  </div>
                );
              })}
              <div ref={bottomRef} />
            </div>

            {/* Reply input */}
            <div style={{ display:'flex', gap:'.6rem', padding:'.8rem 1.2rem', borderTop:'1px solid var(--border)', flexShrink:0 }}>
              <input value={replyText} onChange={e => setReply(e.target.value)}
                placeholder="Type a reply… (Enter to send)"
                onKeyDown={e => e.key === 'Enter' && !e.shiftKey && sendReply()}
                style={{ flex:1 }} />
              <button className="btn btn-primary" onClick={sendReply}>Send</button>
            </div>
          </>
        )}
      </div>

      {/* Show the back button only on mobile widths */}
      <style>{`
        @media (max-width: 860px) {
          .task-back-btn { display: inline-block !important; }
        }
      `}</style>

      {/* New Task Modal */}
      {showNew && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowNew(false)}>
          <div className="modal">
            <div className="modal-header">
              <span className="modal-title">New Task</span>
              <button className="btn-icon" onClick={() => setShowNew(false)}>✕</button>
            </div>
            <label style={{ marginBottom:'1rem' }}>Title *<input value={newTask.title} onChange={setN('title')} autoFocus /></label>
            <label style={{ marginBottom:'1rem' }}>
              Assign To *
              <select value={newTask.tousr} onChange={setN('tousr')}>
                <option value="">-- Select --</option>
                {members.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
              </select>
            </label>
            <label style={{ marginBottom:'1rem' }}>Deadline *<input type="date" value={newTask.deadline} onChange={setN('deadline')} /></label>
            <label style={{ marginBottom:'1.2rem' }}>Details<textarea rows={3} value={newTask.detail} onChange={setN('detail')} /></label>
            <div className="modal-footer">
              <button className="btn btn-primary" onClick={createTask}>Create Task</button>
              <button className="btn btn-ghost" onClick={() => setShowNew(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}